import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";

import { TProduct } from "@/api/services/main/organization/product/dtos/product.dto";
import { FixedProductBadge } from "@/features/organization/product/components/FixedProductBadge";
import { ProductActiveSwitch } from "@/features/organization/product/components/ProductActiveSwitch";
import { getBillingCycleLabel } from "@/features/organization/product/constants/productEnums";
import { useProductMasters } from "@/features/organization/product/hooks/useProductMasters";
import { formatUnitPrice } from "@/features/organization/product/utils/productUtils";
import { text } from "@/theme/colors";

/** Fixed-width column helper (semantic layout honours width on head + body cells). */
const fixedWidth = (width: number) => ({
  size: width,
  muiTableHeadCellProps: { sx: { width, minWidth: width, maxWidth: width } },
  muiTableBodyCellProps: { sx: { width, minWidth: width, maxWidth: width } },
});

/**
 * Column definitions for the 商品設定 list. Mirrors the screenshot:
 *   商品名 (+ 固定商品 pill, expands) | 請求種別 88 | 税率 74 | 単価（税抜）140 | 商品設定 90 (switch)
 */
export const useProductColumns = (onToggleActive: (id: number, nextActive: boolean) => Promise<boolean>) => {
  const { getTaxRateDisplayName } = useProductMasters();
  return useMemo<MRT_ColumnDef<TProduct>[]>(
    () => [
      {
        id: "name",
        header: "商品名",
        accessorKey: "name",
        // 商品名 expands to fill the remaining space.
        muiTableHeadCellProps: { sx: { width: "auto" } },
        muiTableBodyCellProps: { sx: { width: "auto" } },
        Cell: ({ row }) => (
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="body14" color={text.primary} sx={{ p: 0 }}>
              {row.original.name}
            </Typography>
            {row.original.isDefault && <FixedProductBadge />}
          </Stack>
        ),
      },
      {
        id: "billingCycle",
        header: "請求種別",
        ...fixedWidth(88),
        Cell: ({ row }) => getBillingCycleLabel(row.original.billingCycle),
      },
      {
        id: "taxRate",
        header: "税率",
        ...fixedWidth(74),
        Cell: ({ row }) => getTaxRateDisplayName(row.original.taxRateId),
      },
      {
        id: "unitPrice",
        header: "単価（税抜）",
        ...fixedWidth(140),
        Cell: ({ row }) => formatUnitPrice(row.original.unitPrice, row.original.unit),
      },
      {
        id: "isActive",
        header: "商品設定",
        ...fixedWidth(90),
        // Prevent the row click (navigate to detail) from firing when toggling the switch.
        muiTableBodyCellProps: { sx: { width: 90, minWidth: 90, maxWidth: 90 }, onClick: (e) => e.stopPropagation() },
        Cell: ({ row }) => (
          <ProductActiveSwitch productId={row.original.id} isActive={row.original.isActive} onToggle={onToggleActive} />
        ),
      },
    ],
    [onToggleActive, getTaxRateDisplayName],
  );
};
